import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import ImageGallery, { type GalleryImage } from '../components/ImageGallery'

const API_BASE = import.meta.env.PROD
  ? 'https://derm-api.brian-mabry-edwards.workers.dev'
  : ''

interface Patient {
  id: string
  first_name: string
  last_name: string
  date_of_birth?: string
  notes?: string
  created_at: string
  updated_at: string
}

interface PatientImage {
  id: string
  storage_path: string
  filename: string
  content_type: string
  created_at: string
}

function transformToGalleryImages(images: PatientImage[]): GalleryImage[] {
  return images.map((img) => ({
    id: img.id,
    url: `${API_BASE}/api/v1/images/${img.id}/url`,
    filename: img.filename,
    createdAt: img.created_at,
  }))
}

export default function PatientDetail() {
  const { id } = useParams<{ id: string }>()
  const [patient, setPatient] = useState<Patient | null>(null)
  const [images, setImages] = useState<GalleryImage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return

    const fetchPatient = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Fetch patient details
        const patientResponse = await fetch(`${API_BASE}/api/v1/patients/${id}`)
        if (!patientResponse.ok) {
          if (patientResponse.status === 404) {
            throw new Error('Patient not found')
          }
          throw new Error(`Failed to fetch patient: ${patientResponse.status}`)
        }
        const patientData = await patientResponse.json()
        setPatient(patientData.patient)

        // Fetch patient images
        const imagesResponse = await fetch(`${API_BASE}/api/v1/patients/${id}/images`)
        if (imagesResponse.ok) {
          const imagesData = await imagesResponse.json()
          setImages(transformToGalleryImages(imagesData.images || []))
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load patient')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPatient()
  }, [id])

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">Loading patient...</div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
          <h3 className="text-red-800 font-bold text-lg">ERROR</h3>
          <pre className="text-red-700 text-sm mt-2 whitespace-pre-wrap font-mono">{error}</pre>
        </div>
        <Link to="/patients" className="text-primary-600 hover:text-primary-800">
          ← Back to Patients
        </Link>
      </div>
    )
  }

  if (!patient) {
    return (
      <div className="space-y-6">
        <p className="text-gray-500">Patient not found.</p>
        <Link to="/patients" className="text-primary-600 hover:text-primary-800">
          ← Back to Patients
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/patients" className="text-primary-600 hover:text-primary-800 text-sm mb-2 inline-block">
            ← Back to Patients
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {patient.first_name} {patient.last_name}
          </h1>
        </div>
        <Link
          to={`/upload?patient=${patient.id}`}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          Upload Images
        </Link>
      </div>

      {/* Patient Info */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Patient Information</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Name</dt>
            <dd className="text-gray-900">{patient.first_name} {patient.last_name}</dd>
          </div>
          {patient.date_of_birth && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
              <dd className="text-gray-900">{patient.date_of_birth}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">Created</dt>
            <dd className="text-gray-900">
              {new Date(patient.created_at).toLocaleDateString()}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
            <dd className="text-gray-900">
              {new Date(patient.updated_at).toLocaleDateString()}
            </dd>
          </div>
        </dl>
        {patient.notes && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <dt className="text-sm font-medium text-gray-500 mb-1">Notes</dt>
            <dd className="text-gray-900 whitespace-pre-wrap">{patient.notes}</dd>
          </div>
        )}
      </div>

      {/* Images */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Images</h2>
          <span className="text-sm text-gray-500">{images.length} image{images.length !== 1 ? 's' : ''}</span>
        </div>

        <ImageGallery
          images={images}
          columns={4}
          emptyMessage={
            <div className="text-center py-4">
              <p className="text-gray-500 mb-4">No images uploaded yet.</p>
              <Link
                to={`/upload?patient=${patient.id}`}
                className="text-primary-600 hover:text-primary-800 font-medium"
              >
                Upload first image
              </Link>
            </div>
          }
        />
      </div>

      {/* Patient ID for debugging */}
      <div className="text-center text-xs text-gray-400">
        Patient ID: {patient.id}
      </div>
    </div>
  )
}
